// CorporateUBOForm.js (same as ManagementStructure version)
import React, { useEffect, useRef, useCallback, useState } from "react";
import { useUBO } from "../../../hooks/useUBO";
import { UBOForm } from "../ubo/uboForm";
import { SubHeadWithButton } from "../../ui/subHeadWithButton";
import { diligenceObservable } from "../../../observers/deligenceObservable";

export const CorporateUBOForm = React.memo(({ index, onRemoveUBO, uboId, uboData, onUpdate, isReadOnly, isReport }) => {

  // console.log("CorporateUBOForm render", index, uboData, uboId);

  const [uboState, updateUBO, setUBOState] = useUBO({id: uboId, type: uboData.type, percentage: uboData.percentage, data: uboData.data});
  const isInitialMount = useRef(true);
  // Versions this form has already sent up. The parent applies them one render later,
  // so an incoming copy matching one of these is our own echo, not a parent-side edit.
  const pushedRef = useRef(new Set());

  // The parent replaces its UBO list wholesale after a save/refetch. Without this the
  // copy seeded on mount goes stale and the next edit pushes it back up, wiping
  // answers that were already saved and making added UBOs reappear as they were.
  useEffect(() => {
    const incoming = {id: uboId, type: uboData.type, percentage: uboData.percentage, data: uboData.data};
    const serialised = JSON.stringify(incoming);
    if (serialised === JSON.stringify(uboState)) return;
    // Accepting an echo of an earlier push overwrites edits made since, and the two
    // sides then push at each other forever — what Unit Trust hit, because it sends
    // two updates (nominees, trustees) one render apart on mount.
    if (pushedRef.current.has(serialised)) return;
    setUBOState(incoming);
    // uboState is deliberately not a dependency: this only reacts to parent-side changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uboId, uboData, setUBOState]);

  const [hide, setHide] = useState(true);

  const handleHide = useCallback(() => {
    setHide(prev => !prev);
  }, []);

  useEffect(() => {
    if (!isInitialMount.current) {
      const serialised = JSON.stringify(uboState);
      if (pushedRef.current.size > 30) pushedRef.current.clear();
      pushedRef.current.add(serialised);
      // Only update if uboState has changed
      onUpdate(uboId, uboState);
    }
    isInitialMount.current = false;
  }, [uboState, onUpdate, uboId]);

  return (
    <>
        <SubHeadWithButton
          isReport={isReport}
          heading={`UBO ${index + 1}`}
          buttons={[
              {
                  buttonText: "-",
                  onClick: handleHide,
              },
              { 
              buttonText: "×", 
              onClick: () =>{ if (!isReadOnly) {onRemoveUBO(uboId); diligenceObservable.removeByPrefix("corporate"+index.toString())}},
              style: { background: isReadOnly ? "#ccc" : "red", color: "white" }
              }
        ]}
        />
        {hide && <UBOForm
            renderBy={"corporate"}
            index={index}
            isReadOnly={isReadOnly}
            uboType={uboState.type}
            uboPercentage={uboState.percentage}
            uboData={uboState.data}
            onUBOChange={updateUBO}
            isReport={isReport}
        />}
    </>
  );
});


export const MemoizedCorporateUBOForm = React.memo(CorporateUBOForm, (prevProps, nextProps)=> {
  return (
      prevProps.uboId === nextProps.uboId &&
      prevProps.index === nextProps.index &&
      prevProps.isReadOnly === nextProps.isReadOnly &&
      JSON.stringify(prevProps.uboData) === JSON.stringify(nextProps.uboData) &&
      prevProps.onUpdate === nextProps.onUpdate &&
      prevProps.isReport === nextProps.isReport
  );
});